import { useQuery } from "@tanstack/react-query";
import { TreeDataNode } from "antd";
import { useMemo } from "react";

import { msGraphHelper } from "../../../../helper/ms-graph-helper";
import { useSearchParams } from "../../../../hook/use-search-params";
import { DocumentApiV2 } from "../../../../services/document/document-v2.service";
import { MSDriveItem } from "../../../../types/ms-graph";
import { compactData, toTree } from "../../utils/document";
const lookup: Record<string, MSDriveItem> = {};

const sortData = (a: any, b: any) => {
  if (a.docPosition !== undefined && b.docPosition !== undefined) {
    return a.docPosition - b.docPosition;
  }
  if (a.docPosition !== undefined) return -1;
  if (b.docPosition !== undefined) return 1;
  return new Date(a.createdDateTime).getTime() - new Date(b.createdDateTime).getTime();
};

export const useDocDirectory = () => {
  const { searchParams } = useSearchParams();

  const query = useQuery({
    queryKey: ["folders"],
    queryFn: async () => {
      const response = await DocumentApiV2.getDocumentsMgmtDirectory();

      return response;
    },
  });

  const status = searchParams.get("status");

  const queryNew = useQuery({
    queryKey: ["publish-folders-new", status],
    queryFn: () =>
      status === "publish" ? DocumentApiV2.getDocumentsMgmtDirectoryNew() : DocumentApiV2.getDocumentsTreeNew(),
  });

  const compactedData = useMemo(() => compactData((query?.data ?? []) as any), [query.data]);

  compactedData.forEach((item) => (lookup[item.id] = { ...item, children: [] }));

  const mappedData = compactedData?.map((s: any) => ({
    ParentKey: !lookup[s.parentReference.id] ? null : s.parentReference.id,
    Key: s.id,
    Name: s.name,
    Description: "",
    CreatedDate: s.createdDateTime,
    CreatedBy: s.createdBy.user.email,
    Creator: s.createdBy.user.displayName,
    UpdatedDate: null,
    UpdatedBy: null,
    Updater: null,
    IsDeleted: false,
    Position: s.docPosition,
  }));

  const hashedData = useMemo(() => {
    const map = new Map();
    compactedData.forEach((item) => map.set(item.id, item));

    return map;
  }, [compactedData]);

  const treeData = useMemo(() => {
    const sorted = msGraphHelper.sortNestedTree(msGraphHelper.buildTreeFromDriveItem(compactedData), sortData);
    return msGraphHelper.mapNestedTree<TreeDataNode>(sorted, (item) => ({ title: item.name, key: item.id }));
  }, [compactedData]);

  const treeDataNew = useMemo(() => toTree(queryNew.data?.data || []), [queryNew.data?.data]);

  return {
    ...query,
    data: compactedData,
    treeData: treeDataNew,
    treeDataOld: treeData,
    hashedData,
    isLoading: queryNew.isLoading,
  };
};
